import { ref, computed, watch, type ComputedRef } from 'vue';
import { useFormBuilderStore } from './form-builder.store';
import { useTableEditorStore } from './table-editor.store';

export type AccordionValue = 'table' | 'cell' | 'element';

/**
 * Manages property accordion state based on user selections.
 * Automatically expands the appropriate accordion when elements, cells, or tables are selected.
 * Manual accordion toggles work independently without affecting other accordions.
 */
export function usePropertyAccordions(): {
  accordionValues: ComputedRef<string[]>;
  handleAccordionChange: (values: string[]) => void;
} {
  const formBuilderStore = useFormBuilderStore();
  const tableEditorStore = useTableEditorStore();

  const manuallyOpenAccordions = ref<AccordionValue[]>([]);
  const isManualControl = ref(false);

  const selectedElement = computed(() => formBuilderStore.selectedElement);
  const selectedCell = computed(() => tableEditorStore.selectedCell);
  const selectedCellData = computed(() => tableEditorStore.selectedCellData);
  const isEditingMode = computed(() => tableEditorStore.isEditingMode);
  const activeTable = computed(() => tableEditorStore.activeTable);

  const autoAccordionValue = computed<AccordionValue | null>(() => {
    if (selectedCellData.value?.content && activeTable.value) {
      return 'element';
    }

    if (isEditingMode.value && selectedCell.value && activeTable.value) {
      return 'cell';
    }

    if (selectedElement.value?.type === 'TABLE') {
      return 'table';
    }

    if (selectedElement.value) {
      return 'element';
    }

    return null;
  });

  const accordionValues = computed<string[]>(() => {
    if (isManualControl.value) {
      return manuallyOpenAccordions.value;
    }
    return autoAccordionValue.value ? [autoAccordionValue.value] : [];
  });

  watch(
    [
      () => selectedElement.value?.id,
      () => selectedCell.value?.row,
      () => selectedCell.value?.col,
      () => selectedCellData.value?.content?.id,
      () => activeTable.value?.id,
      () => isEditingMode.value,
    ],
    () => {
      isManualControl.value = false;
      manuallyOpenAccordions.value = [];
    },
    { immediate: false }
  );

  function handleAccordionChange(values: string[]): void {
    isManualControl.value = true;
    manuallyOpenAccordions.value = values.filter((v): v is AccordionValue =>
      v === 'table' || v === 'cell' || v === 'element'
    );
  }

  return {
    accordionValues,
    handleAccordionChange,
  };
}
